<script lang="ts">
  export * from './FilterSelect.types';
</script>

<script lang="ts" setup>
  import { computed } from 'vue';

  import { t } from '../../locale';
  import { FieldProps } from '../Field/Field.types';
  import Field from '../Field/Field.vue';
  import FilterChip from '../FilterChip/FilterChip.vue';
  import { FilterSelectModelValue, FilterSelectOption } from './FilterSelect.types';

  export interface FilterSelectProps extends FieldProps {
    /**
     * The name attribute for the `<input>` elements. It is also used as a unique field to differentiate FilterSelect instances.
     */
    name: string;

    options: FilterSelectOption[];

    modelValue: FilterSelectModelValue;

    /**
     * When true, this prop adds an "All" option for selecting all options.
     */
    canSelectAll?: boolean;
  }

  const props = withDefaults(defineProps<FilterSelectProps>(), {
    canSelectAll: false,
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: FilterSelectModelValue): void;
  }>();

  /**
   * @see: https://vuejs.org/guide/components/v-model.html#component-v-model
   */
  const selected = computed({
    get() {
      return props.modelValue;
    },
    set(value) {
      emit('update:modelValue', value);
    },
  });

  const isAllSelected = computed({
    get() {
      return props.options.length === props.modelValue.length;
    },
    set(isChecked) {
      emit('update:modelValue', isChecked ? props.options.map((option) => option.value) : []);
    },
  });

  function getOptionId(option: FilterSelectOption) {
    return `${props.name}--${option.value}`;
  }

  function isOptionSelected(option: FilterSelectOption) {
    return props.modelValue.includes(option.value);
  }

  function onEnter(enteredValue: FilterSelectOption['value']) {
    const newSelected = selected.value.filter((value) => value !== enteredValue);

    if (newSelected.length === selected.value.length) {
      newSelected.push(enteredValue);
    }

    selected.value = newSelected;
  }

  function onEnterAll() {
    isAllSelected.value = !isAllSelected.value;
  }
</script>

<template>
  <Field v-bind="props" class="stash-filter-select" data-test="stash-filter-select" fieldset>
    <div class="flex flex-wrap gap-1.5">
      <div v-if="props.canSelectAll" :key="`${props.name}--all`">
        <input
          :id="`${props.name}--all`"
          v-model="isAllSelected"
          class="sr-only"
          tabindex="-1"
          type="checkbox"
          value="all"
        />
        <!--  Prevent the native scroll behavior when clicking the label -->
        <label :for="`${props.name}--all`" @click.prevent="onEnterAll">
          <FilterChip :is-selected="isAllSelected" tabindex="0" @keypress.enter="onEnterAll">
            {{ t('ll.all') }}
          </FilterChip>
        </label>
      </div>
      <div v-for="option in props.options" :key="getOptionId(option)">
        <input
          :id="getOptionId(option)"
          v-model="selected"
          class="sr-only"
          tabindex="-1"
          type="checkbox"
          :value="option.value"
        />
        <!--  Prevent the native scroll behavior when clicking the label -->
        <label :for="getOptionId(option)" @click.prevent="onEnter(option.value)">
          <FilterChip :is-selected="isOptionSelected(option)" tabindex="0" @keypress.enter="onEnter(option.value)">
            {{ option.label }}
          </FilterChip>
        </label>
      </div>
    </div>
  </Field>
</template>
